import { LICENCES } from '../../generated/licences'
import { Langtext } from '../../components/Langtext'
import { useSettings } from '../../hooks/queries'

/**
 * Which open-source projects this application uses — visible to everyone.
 *
 * From the log: „haben wir Code aus anderen open source Projekten verwendet?
 * wenn ja, müssten wir das anzeigen - oder? Eigene Kategorie in Einstellungen,
 * für alle sichtbar." And, rightly, a follow-up: „und was ist mit MariaDB?"
 *
 * The question has three answers, and they differ:
 *
 *  - **In the browser** seven packages ship inside the bundle. Naming them is
 *    not politeness: MIT and BSD require the copyright notice and the licence
 *    text to travel with the software, and this software travels on every page
 *    load. The list is generated (`tools/collect-licences.mjs`), never typed —
 *    licence notices carry names and years that nobody may guess.
 *  - **In our own server code, not one foreign line.** `composer.json` asks for
 *    PHP, PDO and mbstring; PHPUnit is a development tool. That is not thrift,
 *    it is the target: shared hosting has no Composer.
 *  - **Underneath, plenty** — and that is the follow-up question. PHP and
 *    MariaDB carry the whole thing. We do not ship them, so no obligation
 *    follows; a list without them would still be dishonest.
 *
 * The interface stays German — the people reading it are.
 */
export function OpenSourceCard() {
  const settings = useSettings()
  const laufzeit = settings.data?.runtime ?? null
  const dienste = settings.data?.services ?? null

  return (
    <>
      <section className="card card--seite">
        <h2 className="card__titel">Diese Anwendung</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Travel Broadcast steht unter der <strong>GPL-3.0-or-later</strong>. Der
          Quelltext und der vollständige Lizenztext liegen im Repository —{' '}
          <a
            href="https://github.com/martinB65/Travel-Broadcast"
            target="_blank"
            rel="noreferrer noopener"
          >
            github.com/martinB65/Travel-Broadcast
          </a>
          .
        </p>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Im Browser</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Diese {LICENCES.length} Projekte stecken in der Oberfläche, die dein
          Gerät lädt. Ihre Lizenzen verlangen, dass Vermerk und Text mitkommen —
          deshalb stehen sie hier vollständig.
        </p>
        <ul className="lizenzen">
          {LICENCES.map((eintrag) => (
            <li key={eintrag.name} className="lizenz">
              <p className="lizenz__kopf">
                <strong>{eintrag.name}</strong> {eintrag.version} ·{' '}
                <span className="lizenz__marke">{eintrag.licence}</span>
              </p>
              {eintrag.homepage !== '' && (
                <p className="feld__hinweis">
                  <a href={eintrag.homepage} target="_blank" rel="noreferrer noopener">
                    {eintrag.homepage}
                  </a>
                </p>
              )}
              {/* The full text, folded away — the same component the long trip
                  reports use. Seven notices unfolded would bury the list. */}
              <Langtext text={eintrag.text} zeilen={4} ab={200}>
                <pre className="lizenz__text">{eintrag.text}</pre>
              </Langtext>
            </li>
          ))}
        </ul>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Auf dem Server</h2>
        <p className="feld__hinweis feld__hinweis--13">
          <strong>Keine fremde Zeile.</strong> Der Servercode kommt ohne
          Bibliotheken aus — das ist keine Sparsamkeit, sondern die Zielumgebung:
          auf geteiltem Webhosting gibt es keinen Composer.
        </p>
        <p className="feld__hinweis feld__hinweis--13">
          Tragen tun ihn aber zwei fremde Projekte. Sie werden{' '}
          <strong>nicht mitgeliefert</strong>, sondern vom Server bereitgestellt:
        </p>
        <ul className="lizenzen">
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>PHP</strong> {laufzeit?.php ?? '—'} ·{' '}
              <span className="lizenz__marke">PHP License</span>
            </p>
            <p className="feld__hinweis">
              <a href="https://www.php.net/license/" target="_blank" rel="noreferrer noopener">
                php.net/license
              </a>
            </p>
          </li>
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>MariaDB</strong> {laufzeit?.database ?? '—'} ·{' '}
              <span className="lizenz__marke">GPL-2.0</span>
            </p>
            <p className="feld__hinweis">
              <a href="https://mariadb.com/kb/en/mariadb-license/" target="_blank" rel="noreferrer noopener">
                mariadb.com/kb/en/mariadb-license
              </a>
            </p>
          </li>
        </ul>
        <p className="feld__hinweis">
          Die Fassungen stehen hier so, wie der Server sie gerade meldet — nicht
          so, wie sie beim Schreiben dieser Zeile galten.
        </p>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Dienste, keine Bibliotheken</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Was nicht als Code hier liegt, aber in der Anwendung sichtbar wird.
        </p>
        <ul className="lizenzen">
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>Kartenkacheln</strong>
            </p>
            {/* The notice comes from `MapSources` by way of the settings, never
                typed here: whoever switches the source switches it with them. */}
            <p
              className="feld__hinweis"
              dangerouslySetInnerHTML={{ __html: settings.data?.map?.attribution ?? '' }}
            />
          </li>
          {dienste?.gif === true && (
            <li className="lizenz">
              <p className="lizenz__kopf">
                <strong>GIPHY</strong>
              </p>
              <p className="feld__hinweis">
                Die GIF-Auswahl in einer Antwort kommt von GIPHY.
              </p>
            </li>
          )}
          {(dienste?.place === true || dienste?.weather === true) && (
            <li className="lizenz">
              <p className="lizenz__kopf">
                <strong>Apple Maps &amp; Weather</strong>
              </p>
              <p className="feld__hinweis">
                Ortsnamen und Wetter zu einem Punkt holt der Server bei Apple.
                Weather-Daten stammen von{' '}
                <a
                  href="https://developer.apple.com/weatherkit/data-source-attribution/"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Apple Weather
                </a>
                .
              </p>
            </li>
          )}
        </ul>
      </section>
    </>
  )
}
